import { useMemo, useEffect, type MouseEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/features/toast";
import { useCart } from "@/features/cart";
import { useProductDetail as useProductDetailAPI } from "@/api/hooks";
import { queryService } from "@/api/services/query-service";
import { useFavoriteToggle } from "@/features/favorites/hooks/use-favorite-toggle";
import { useFavoriteProductsStore } from "@/features/favorites/store/favorite-products-store";
import { useProductDetailStore } from "../store/product-detail-store";
import { useProductDetailModalStore } from "../store/product-detail-modal-store";
import useAuthStore from "@/store/use-auth-store";
import { isModifiedClick } from "@/lib/link-utils";
import { useCommercePaths } from "@/modules";
import {
  detectPlatform,
  getCurrencySymbol,
  // formatRating,
  // formatReviewCount,
} from "@/lib/product-config";

const formatPrice = (
  price: number | undefined,
  currency: string,
): { formatted: string; prefix: string; suffix: string } => {
  if (!price) {
    return { formatted: "-", prefix: "", suffix: "" };
  }

  const formattedPrice = price.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // TL ise sağda göster
  if (currency === "TL" || currency === "₺") {
    return {
      formatted: `${formattedPrice} TL`,
      prefix: "",
      suffix: " TL",
    };
  }

  // USD, EUR gibi diğer para birimleri için sol tarafta sembol
  const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
  };

  const symbol = currencySymbols[currency] || currency;
  return {
    formatted: `${symbol}${formattedPrice}`,
    prefix: symbol,
    suffix: "",
  };
};

export const useProductDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = useCommercePaths();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const { success, error } = useToast();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuthStore();
  const {
    isOpen: isModalOpen,
    productId: modalProductId,
    offerId: modalOfferId,
    closeModal: closeModalModal,
  } = useProductDetailModalStore();

  // Get product ID from route params, search params, or modal store
  const productId = routeId || searchParams.get("id") || modalProductId;

  // offerId URL'den oku (farklı satıcı için)
  const offerId = searchParams.get("offerId") || modalOfferId || null;

  // preview token for staging merchants
  const preview = searchParams.get("preview") || null;

  // API Hooks - Yeni product detail API'den tüm bilgileri çekiyoruz
  const { data: productResponse, isLoading: isProductLoading } =
    useProductDetailAPI(productId, offerId, preview);

  const productData = productResponse?.data;

  // Favori durumu: detay yanıtı gerçeği söyler, store onu hatırlar — böylece
  // aynı ürünün feed/arama kartındaki kalbi de dolu çizilir.
  const seedFavorite = useFavoriteProductsStore((state) => state.seedFavorite);
  const {
    isFavorite,
    isPending: isFavoritePending,
    toggle: toggleFavorite,
  } = useFavoriteToggle(productId);

  useEffect(() => {
    if (productId && typeof productData?.isFavorite === "boolean") {
      seedFavorite(productId, productData.isFavorite);
    }
  }, [productId, productData?.isFavorite, seedFavorite]);

  // Store State
  const {
    selectedSize,
    activeImageIdx,
    showReviewsExpanded,
    quantity,
    setSelectedSize,
    setActiveImageIdx,
    toggleReviewsExpanded,
    nextImage,
    prevImage,
    incrementQuantity,
    decrementQuantity,
    reset,
  } = useProductDetailStore();

  // Mapped Product Data
  const mappedProduct = useMemo(() => {
    if (!productData) {
      return {
        image: "",
        hoverImage: "",
        title: "",
        brand: "",
        price: undefined,
        oldPrice: undefined,
        discountPct: 0,
        currency: getCurrencySymbol(),
        formattedPrice: { formatted: "-", prefix: "", suffix: "" },
        formattedOldPrice: { formatted: "-", prefix: "", suffix: "" },
        url: "",
        colorsHex: [],
        colorsLabels: [],
        sizes: [],
        allSizes: [], // Tüm beden seçenekleri
        inStock: true, // Default true to avoid false "out of stock" badge during loading
        lowStock: undefined,
        hasFastShipping: false,
        hasFreeShipping: false,
        images: [],
        platform: "Shop" as const,
        platformIcon: "🛍️",
        rating: undefined,
        reviewCount: undefined,
        attrs: [],
        colorGlobalName: "",
        departmentName: "",
        categories: [],
        seller: null,
        stockCount: undefined,
        detailHTML: "",
        additionalDetailHTML: "",
        firstVariantGroup: [],
        secondVariantGroup: [],
        slicerGroups: [],
        familyId: undefined,
      };
    }

    // Images
    const image = productData?.images?.[0]?.imageUrl || "";
    const hoverImage = productData?.images?.[1]?.imageUrl || image;
    const images = (productData?.images || [])
      .map((img) => img?.imageUrl)
      .filter(Boolean);

    // Basic Info
    const title = (productData?.displayName || productData?.name || "").trim();
    const brand = productData?.brandName || "";

    // Pricing - Yeni API yapısı: listPrice, salePrice, campaignPrice
    const listPrice = productData?.prices?.listPrice?.value;
    const salePrice = productData?.prices?.salePrice?.value;
    const campaignPrice = productData?.prices?.campaignPrice?.value;

    // Effective price: campaignPrice > salePrice > listPrice
    const effectivePrice = campaignPrice || salePrice || listPrice;

    const price = effectivePrice ?? undefined;
    const oldPrice =
      listPrice && listPrice > (effectivePrice || 0) ? listPrice : undefined;
    const discountPct =
      oldPrice && price && oldPrice > price
        ? Math.round(((oldPrice - price) / oldPrice) * 100)
        : 0;

    const currencyValue = productData?.prices?.listPrice?.currency || "TL";
    const currency = currencyValue;

    // Format prices based on currency type
    const formattedPrice = formatPrice(price, currency);
    const formattedOldPrice = formatPrice(oldPrice, currency);

    // Extract colors and sizes from variant groups
    const firstVariantGroup = productData?.firstVariantGroup || [];
    const secondVariantGroup = productData?.secondVariantGroup || [];

    // Seçili renk ve beden
    const selectedColor =
      firstVariantGroup.find((v) => v.isSelected)?.attributeValue || "";
    const selectedSize =
      secondVariantGroup.find((v) => v.isSelected)?.attributeValue || "";

    // Tüm renk ve beden seçenekleri
    const allColors = firstVariantGroup
      .map((v) => v.attributeValue)
      .filter(Boolean);
    const allSizes = secondVariantGroup
      .map((v) => v.attributeValue)
      .filter(Boolean);

    const colorsHex: string[] = [];
    const colorsLabels = allColors;
    const sizes = selectedSize ? [selectedSize] : [];

    // Stock Info
    const inStock = productData?.inStock ?? false;
    const stockCount = productData?.stockCount;
    const lowStock = stockCount && stockCount < 10 ? true : undefined;

    // URLs & Platform
    const url = productResponse?.url || "";
    const platformConfig = detectPlatform(url);

    // Categories
    const categories = productData?.categories || [];
    const departmentName = categories[0]?.name || "";

    // Seller Info
    const seller = {
      id: "",
      name: productData?.sellerName || "",
      logoUrl: productData?.sellerLogoUrl,
      seoName: productData?.sellerSeoName || "",
      seoUrl: "",
    };

    // Product Details (HTML content)
    const detailHTML = productData?.detailHTML || "";
    const additionalDetailHTML = productData?.additionalDetailHTML || "";

    return {
      image,
      hoverImage,
      title,
      brand,
      price,
      oldPrice,
      discountPct,
      currency,
      formattedPrice,
      formattedOldPrice,
      url,
      colorsHex,
      colorsLabels,
      sizes,
      allSizes, // Tüm beden seçenekleri
      inStock,
      lowStock,
      hasFastShipping: false,
      hasFreeShipping: false,
      images,
      platform: platformConfig.name as "Amazon" | "Shopify" | "Shop",
      platformIcon: platformConfig.icon,
      // Rating ve reviewCount gerçek data yoksa undefined
      rating: undefined, // TODO: Gerçek data geldiğinde kullan
      reviewCount: undefined, // TODO: Gerçek data geldiğinde kullan
      // Product details fields
      attrs: productData?.attributes || [],
      colorGlobalName: selectedColor || "",
      departmentName,
      categories,
      seller,
      stockCount,
      // New fields from API
      detailHTML,
      additionalDetailHTML,
      firstVariantGroup,
      secondVariantGroup,
      slicerGroups: productData?.slicerGroups || [],
      familyId: productData?.familyId,
    };
  }, [productData, productResponse]);

  // Initialize selected size with the selected one from API or first available size
  useEffect(() => {
    if (!selectedSize && mappedProduct.secondVariantGroup) {
      // Önce API'den seçili olanı bul
      const apiSelectedSize = mappedProduct.secondVariantGroup.find(
        (v) => v.isSelected,
      )?.attributeValue;
      if (apiSelectedSize) {
        setSelectedSize(apiSelectedSize);
      } else if (mappedProduct.allSizes && mappedProduct.allSizes.length > 0) {
        // API'den seçili yoksa ilk bedeni seç
        setSelectedSize(mappedProduct.allSizes[0]);
      }
    }
  }, [
    mappedProduct.secondVariantGroup,
    mappedProduct.allSizes,
    selectedSize,
    setSelectedSize,
  ]);

  // Reset store when component unmounts or product changes
  useEffect(() => {
    return () => {
      reset();
    };
  }, [productId, reset]);

  // Business Actions
  const handleAddToCart = async () => {
    try {
      if (!productData?.offerId) {
        return;
      }

      // Yeni basitleştirilmiş API yapısı - sadece offerId ve quantity
      await addToCart({
        offerId: productData.offerId,
        quantity,
      });

      // Başarılı ekleme sonrası modal'daysa kapat
      if (isModalOpen) {
        closeModalModal();
      }
    } catch (errorResponse: any) {
      // Error handled by cart hook
    }
  };

  const handleShare = () => {
    if (navigator.share && mappedProduct.url) {
      navigator.share({
        title: mappedProduct.title,
        url: mappedProduct.url,
      });
    } else if (mappedProduct.url) {
      navigator.clipboard.writeText(mappedProduct.url);
      success(t("productDetail.linkCopied"), {
        title: t("productDetail.share"),
      });
    }
  };

  const handleBuyNow = async () => {
    if (!productData?.offerId) return;

    try {
      // Checkout'a gitmeden önce offer'ın hemen al için uygun olduğunu doğrula.
      const preCheckResponse: any = await queryService.getAllCart(
        productData.offerId,
        quantity,
      );
      if (
        preCheckResponse?.code === "offer_unavailable" ||
        preCheckResponse?.success === false
      ) {
        error(
          preCheckResponse?.message || t("cart.buyNowUnavailable"),
          {
            title: t("cart.cartChangedTitle"),
            duration: 5000,
            position: "bottom-right",
          },
        );
        return;
      }

      // Modal açıksa kapat
      if (isModalOpen) {
        const { closeModal } = useProductDetailModalStore.getState();
        closeModal();
      }

      // offerId ve seçili adet ile checkout'a yönlendir
      navigate(`/checkout?offerId=${productData.offerId}&quantity=${quantity}`);
    } catch (preCheckError: any) {
      error(
        preCheckError?.response?.data?.message ||
          preCheckError?.message ||
          t("cart.buyNowUnavailable"),
        {
          title: t("cart.cartChangedTitle"),
          duration: 5000,
          position: "bottom-right",
        },
      );
    }
  };

  // 🔗 Navigasyonu artık <Link> üstleniyor (bkz. ProductInfoSection); burada
  // sadece modal kapatma yan etkisi kalıyor ve modifier'lı tıklamada (yeni
  // sekme) atlanıyor.
  const handleViewFullPage = (e: MouseEvent) => {
    if (isModifiedClick(e)) return; // yeni sekmede açılıyor, modal açık kalmalı
    const { closeModal } = useProductDetailModalStore.getState();
    closeModal();
  };

  const handleVariantSelect = (newProductId: string) => {
    // Modal'daysa modal'ı yeni productId ile aç
    if (modalProductId) {
      const { openModal } = useProductDetailModalStore.getState();
      openModal(newProductId);
    }
    // Page'deyse yeni product'a navigate et
    else if (routeId) {
      navigate(paths.product(newProductId), { replace: true });
    }
    // Search params'daysa (modal gibi davranıyor)
    else if (searchParams.get("id")) {
      navigate({
        search: `?id=${newProductId}`,
      });
    }
  };

  // Farklı satıcıya geçiş - aynı ürün sayfası, farklı offerId
  const handleSellerSelect = (newOfferId: string) => {
    if (!productId) return;
    if (modalProductId) {
      // Modal için: aynı productId ile offerId'yi search param olarak geç
      // Modal kendi state'ini kullandığı için şimdilik yeni sayfaya yönlendir
      navigate(paths.product(productId, { offerId: newOfferId }));
      const { closeModal } = useProductDetailModalStore.getState();
      closeModal();
    } else if (routeId) {
      navigate(paths.product(routeId, { offerId: newOfferId }), { replace: true });
    } else if (searchParams.get("id")) {
      navigate({ search: `?id=${productId}&offerId=${newOfferId}` });
    }
  };

  const handlePrevImage = () => {
    if (mappedProduct.images && mappedProduct.images.length > 0) {
      prevImage(mappedProduct.images.length);
    }
  };

  const handleNextImage = () => {
    if (mappedProduct.images && mappedProduct.images.length > 0) {
      nextImage(mappedProduct.images.length);
    }
  };

  const handleImageClick = (idx: number) => {
    setActiveImageIdx(idx);
  };

  return {
    // Product Data
    product: mappedProduct,
    productId, // 🆕 Expose productId for queries
    brand: productData
      ? {
          id: productData.brandId,
          name: productData.brandName,
          seoName: productData.brandSeoName,
        }
      : null,
    seller: mappedProduct.seller,

    // Loading States
    isProductLoading,
    isLoading: isProductLoading,

    // UI States
    selectedSize,
    activeImageIdx,
    showReviewsExpanded,
    quantity,

    // Size Actions
    onSizeSelect: setSelectedSize,

    // Image Navigation Actions
    onPrevImage: handlePrevImage,
    onNextImage: handleNextImage,
    onImageClick: handleImageClick,

    // Reviews Actions
    onToggleReviews: toggleReviewsExpanded,

    // Quantity Actions
    onIncrementQuantity: incrementQuantity,
    onDecrementQuantity: decrementQuantity,

    // Business Actions
    onAddToCart: handleAddToCart,
    onBuyNow: handleBuyNow,
    onToggleFavorite: () => void toggleFavorite(),
    isFavorite,
    isFavoritePending,
    onShare: handleShare,

    // Variant Actions
    onVariantSelect: handleVariantSelect,

    // Seller Actions
    onSellerSelect: handleSellerSelect,
    otherSellers: productData?.otherSellers ?? [],
    currentOfferId: productData?.offerId ?? null,

    // Navigation Actions
    onViewFullPage: handleViewFullPage,
    // onTryOn kaldırıldı: /wardrobe?tryOnProductId=... artık ProductInfoSection'da
    // doğrudan <Link>'e bağlanıyor (bkz. productId dönüş değeri).

    // Auth Status
    isAuthenticated: isAuthenticated(),
  };
};
